import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'i18n'
import { FormBuilder } from 'components/FormBuilder'
import { useForm } from 'react-hook-form'
import { generateDynamicYupSchema } from 'components/FormBuilder/generateYupSchemaFromJson'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { UserVerification } from 'components/UserVerification/UserVerification'
import { Dialog, IconButton, CircularProgress } from '@mui/material'
import { Turnstile } from '@marsidev/react-turnstile'
import { LegalTerm } from 'components/FormBuilder/Fields/LegalTerm'
import { createCheckboxValidationSchema } from 'components/forms/validations'
import closeLogo from 'images/icon-close.svg'
import incomOfferFormFieldsJson from './incomOfferFormFields.json'
import * as styles from './IncommOfferForm.module.scss'
import { Offer } from '../../types/offer'
import {
  IncommBarcodeFormProps,
  normalizeCheckboxesForForm,
} from '../../utils'
import {
  getBannerStoreById,
  getStoreGroups,
} from '../../constants/banner-stores'
import {
  generateIncommTokenService,
  validateTokenAndGenerateBarcode,
} from 'services/incomm/token'
import * as Yup from 'yup'

export interface IncommStepProps {
  offer: Offer
  incommBarcodeForm?: IncommBarcodeFormProps
}

export const IncommOfferForm = ({
  offer,
  incommBarcodeForm,
}: IncommStepProps) => {
  const { t } = useTranslation(['incommOffer', 'common'])
  const {
    ctaLabel = 'Get Offer',
    selectedStoreId = '',
    availableStoreIds = [],
    onSubmit: onSubmitProp,
    formError,
    skipVerification,
    autoSendEmail,
    testToken,
    checkboxes = [],
    onStatusModalOpen,
    defaultEmail = '',
  } = incommBarcodeForm
  if (!incommBarcodeForm) {
    throw new Error('Barcode form is required')
  }

  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)
  const [isInProgress, setIsInProgress] = useState(false)
  const [storeGroups, setStoreGroups] = useState<any[]>([])
  const turnstileRef = useRef(null)
  const offerRetailerIds = useMemo(
    () =>
      Array.isArray(offer.retailerIds)
        ? offer.retailerIds.map(id => Number(id)).filter(Number.isFinite)
        : [],
    [offer.retailerIds],
  )
  const checkboxFields = useMemo(
    () => normalizeCheckboxesForForm(checkboxes),
    [checkboxes],
  )

  // Load store groups data asynchronously and filter by explicit banner-store
  // IDs first, then by the offer's retailer IDs when no override is configured.
  useEffect(() => {
    const loadStoreGroups = async () => {
      const allGroups = await getStoreGroups()

      if (availableStoreIds.length > 0 || offerRetailerIds.length > 0) {
        // Filter groups to only include stores that are in availableStoreIds
        const filteredGroups = allGroups
          .map(group => ({
            ...group,
            bannerStores: group.bannerStores.filter(store => {
              if (availableStoreIds.length > 0) {
                return availableStoreIds.includes(store.bannerStoreId)
              }

              const retailerIds = store.retailerIds || [store.retailerId]
              return retailerIds.some(id => offerRetailerIds.includes(id))
            }),
          }))
          .filter(group => group.bannerStores.length > 0)

        setStoreGroups(filteredGroups)
      } else {
        // If no specific stores are available, show all stores
        setStoreGroups(allGroups)
      }
    }
    loadStoreGroups()
  }, [availableStoreIds, offerRetailerIds])

  // Dynamically generate dropdown options from store groups
  const dynamicFormFields = useMemo(() => {
    const dropdownOptions: any[] = []

    storeGroups.forEach(group => {
      // Add stores in this group with group identifier
      group.bannerStores.forEach(store => {
        dropdownOptions.push({
          value: store.bannerStoreId,
          label: store.name,
          group: group.storeGroupName,
        })
      })
    })

    // Clone the fields and update the selectedBannerStoreId field options
    const fields = incomOfferFormFieldsJson.fields.map(field => {
      if (field.name === 'selectedBannerStoreId') {
        return {
          ...field,
          options: dropdownOptions,
        }
      }
      return field
    })

    return fields
  }, [storeGroups])

  const jsonValidationYup = useMemo(() => {
    const baseSchema = generateDynamicYupSchema(dynamicFormFields)
    const schemaObject: any = { ...baseSchema.fields }
    const checkboxValidations = createCheckboxValidationSchema(checkboxFields)

    return Yup.object().shape({ ...schemaObject, ...checkboxValidations })
  }, [dynamicFormFields, checkboxFields])

  // Build default values including checkboxes
  const defaultValues = useMemo(() => {
    const defaults: any = {
      email: defaultEmail || '',
      selectedStoreId: selectedStoreId,
      availableStoreIds: availableStoreIds,
    }

    // Add checkbox default values
    checkboxFields.forEach(checkbox => {
      defaults[checkbox.fieldName] = false
    })

    return defaults
  }, [selectedStoreId, availableStoreIds, checkboxFields, defaultEmail])

  const form = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: useYupValidationResolver(jsonValidationYup),
  })

  const {
    handleSubmit,
    register,
    getValues,
    control,
    formState: { errors },
  } = form

  const onEmailVerifiedConfirm = async ({ values }) => {
    try {
      // Barcode generation already happened during validation!
      // The custom validation service returned the barcode data
      const valuesFromForm = getValues()

      // Collect checkbox values
      const checkboxValues: Record<string, boolean> = {}
      checkboxFields.forEach(checkbox => {
        checkboxValues[checkbox.id] = valuesFromForm[checkbox.fieldName] || false
      })

      // Pass the barcode response to parent via onSubmit callback
      await onSubmitProp({
        ...values,
        selectedStoreId: valuesFromForm.selectedBannerStoreId || null,
        checkboxes: checkboxValues,
      })

      setIsInProgress(false)
      handleClose()
    } catch (error) {
      console.error('Error processing barcode:', error)
      setIsInProgress(false)
    }
  }

  const getCheckboxesAccepted = () => {
    const valuesFromForm = getValues()
    return checkboxFields.map(checkbox => ({
      checkboxId: checkbox.id,
      label: checkbox.label,
      required: Boolean(checkbox.required),
      accepted: Boolean(valuesFromForm[checkbox.fieldName]),
    }))
  }

  const onValidationError = (error: any) => {
    console.log('onValidationError called with:', error)

    // Close the verification dialog
    handleClose()

    // Check error status and message
    // doPost2ErrorHandling throws: { status, success: false, detail: "error message" }
    const errorStatus = error?.status
    const errorMessage = error?.detail || error?.message || String(error)
    console.log('Parsed error message:', errorMessage)

    // Check for service unavailable (503)
    if (errorStatus === 503) {
      onStatusModalOpen?.('service_unavailable')
      return
    }

    const errorString = String(errorMessage).toLowerCase()
    if (errorString.includes('cliplimitexceeded') ||
      errorString.includes('clip limit exceeded')) {
      onStatusModalOpen?.('clip_limit_exceeded')
    } else {
      // Show generic error modal for other errors
      onStatusModalOpen?.('service_unavailable')
    }
  }

  const onSubmit = async data => {
    setIsInProgress(true)

    const selectedStore = await getBannerStoreById(data.selectedBannerStoreId)
    const barcodeFormat = selectedStore?.barcodeFormat || 'CODE_128'

    if (skipVerification) {
      try {
        const response = await validateTokenAndGenerateBarcode({
          email: data.email,
          token: testToken || 'SKIP_VERIFICATION',
          offerId: String(offer.id),
          selectedBannerStoreId: data.selectedBannerStoreId,
          barcodeFormat,
          autoSendEmail,
          checkboxesAccepted: getCheckboxesAccepted(),
        })

        onEmailVerifiedConfirm({ values: response })
      } catch (error: any) {
        console.error('Error generating barcode:', error)

        const errorStatus = error?.status
        const errorMessage = error?.message || error?.detail || String(error)

        // Check for service unavailable (503)
        if (errorStatus === 503) {
          onStatusModalOpen?.('service_unavailable')
        } else if (errorMessage.toLowerCase().includes('cliplimitexceeded') ||
          errorMessage.toLowerCase().includes('clip limit exceeded')) {
          onStatusModalOpen?.('clip_limit_exceeded')
        } else {
          // Show generic error modal for other errors
          onStatusModalOpen?.('service_unavailable')
        }

        setIsInProgress(false)
      }
      return
    }

    if (!turnstileRef.current?.getResponse()) {
      console.error('Turnstile token not available')
      setIsInProgress(false)
      return
    }

    setVerificationDialogOpen(true)
  }

  const handleClose = () => {
    setVerificationDialogOpen(false)
    setIsInProgress(false)
    turnstileRef.current?.reset()
  }

  return (
    <div className={styles.formContainer}>
      <FormBuilder
        className={styles.incommForm}
        jsonFieldOnForm={true}
        fieldStyle={{}}
        fields={dynamicFormFields}
        formHeader={null}
        form={form}
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        setFieldValue={form.setValue}
        disableValidation={true}
        disabledFields={[]}
      >
        {/* Render checkboxes if provided */}
        {checkboxFields.length > 0 && (
          <div className={styles.checkboxesContainer}>
            {checkboxFields.map(checkbox => (
              <LegalTerm
                key={checkbox.fieldName}
                name={checkbox.fieldName}
                label={checkbox.label}
                register={register}
                error={errors[checkbox.fieldName]}
                required={checkbox.required}
                control={control}
                defaultChecked={false}
                disabled={false}
              />
            ))}
          </div>
        )}
        {/* Turnstile component visible in form - token captured before modal opens */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Turnstile
            ref={turnstileRef}
            siteKey={process.env.VISIBLE_TURNSTILE_SITE_KEY}
          />
          <button
            className={styles.submitButton}
            disabled={isInProgress}
            type="submit"
          >
            {isInProgress ? (
              <CircularProgress color="inherit" size={18} />
            ) : (
              <span dangerouslySetInnerHTML={{ __html: ctaLabel }} />
            )}
          </button>
        </div>
      </FormBuilder>
      {formError && <div className={styles.error}>{formError}</div>}
      <Dialog
        onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}
        open={verificationDialogOpen}
      >
        <IconButton
          aria-label={t('common:close')}
          onClick={handleClose}
          sx={{
            right: 8,
            top: 8,
            alignSelf: 'end',
          }}
        >
          <img src={closeLogo} alt={t('common:close')} />
        </IconButton>
        <UserVerification
          hasTurnstile={false}
          onConfirm={onEmailVerifiedConfirm}
          onValidationError={onValidationError}
          onTokenAlreadyRequested={() =>
            onStatusModalOpen?.('already_requested')
          }
          customGenerateTokenService={generateIncommTokenService}
          customValidateTokenService={validateTokenAndGenerateBarcode}
          tokenServicePayload={{
            offerId: String(offer.id),
            selectedBannerStoreId: getValues().selectedBannerStoreId,
            barcodeFormat:
              storeGroups
                .flatMap(group => group.bannerStores)
                .find(
                  store =>
                    store.bannerStoreId === getValues().selectedBannerStoreId,
                )?.barcodeFormat || 'CODE_128',
            turnstileToken: turnstileRef.current?.getResponse(),
            autoSendEmail,
            checkboxesAccepted: getCheckboxesAccepted(),
          }}
          {...getValues()}
        />
      </Dialog>
    </div>
  )
}
